import React, { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PogLogo from "../components/PogLogo";
import { GOV_WARNING } from "../data/content";

const CANONICAL = "https://pogspirits.com";

const ASSETS = [
  {
    id: "logo-png",
    title: "POG Spirits Logo",
    format: "PNG · Transparent · 1497×907",
    url: "/brand/pog-logo.png",
    preview: "/brand/pog-logo.png",
    tone: "logo",
  },
  {
    id: "vodka-shot",
    title: "POG Vodka — Product Shot",
    format: "JPG · 1024×1536",
    url: "/products/pog-vodka.jpg",
    preview: "/products/pog-vodka.jpg",
    tone: "product",
  },
  {
    id: "can-shot",
    title: "POG Spiked Island Punch — Can Shot",
    format: "JPG · 937×1400",
    url: "/products/pog-can.jpg",
    preview: "/products/pog-can.jpg",
    tone: "product",
  },
  {
    id: "og-image",
    title: "Open Graph Brand Image",
    format: "JPG · 1200×630 · Social share",
    url: "/og-image.jpg",
    preview: "/og-image.jpg",
    tone: "og",
  },
];

const BOILERPLATE_SHORT = `POG Spirits is an independent spirits brand launching in 2026, created in partnership with Meadow Gold and inspired by the iconic POG (Passion Orange Guava) juice invented at Meadow Gold in Hawai‘i in 1971. The debut range includes POG Vodka (750 ml, 35% ABV) and POG Spiked Island Punch RTD (11.5 fl oz, 6% ABV). Distributed across Hawai‘i by Reyes and Island Distributing. Made in Hawai‘i. 21+.`;

const BOILERPLATE_LONG = `In 1971, a creamery worker at Meadow Gold blended three island fruits — passion fruit, orange, and guava — and quietly invented one of the most beloved flavors in the Pacific: POG. For fifty-five years the flavor lived on cartons, plate lunches, and childhood memories across the Hawaiian islands. In 2026, POG Spirits — an independent spirits brand created in partnership with Meadow Gold — brings that flavor into the bottle: POG Vodka (750 ml, 35% ABV, 70 proof) and POG Spiked Island Punch RTD (11.5 fl oz can, 6% ABV) — crafted with real passion fruit, orange, and guava. Distributed across Hawai‘i by Reyes and Island Distributing. Icons evolve. So we did.`;

const FACT_SHEET = [
  ["Brand", "POG Spirits — independent spirits brand"],
  ["Partnership", "Created in partnership with Meadow Gold"],
  ["Distribution (Hawai‘i)", "Reyes / Island Distributing"],
  ["Launch year", "2026"],
  ["Origin", "Hawai‘i, USA"],
  ["Products", "POG Vodka 750ml (35% ABV) · POG Spiked Island Punch RTD 11.5 fl oz (6% ABV)"],
  ["Flavor heritage", "Passion fruit, Orange, Guava (POG) — invented at Meadow Gold in 1971"],
  ["Tagline", "Icons evolve. So we did."],
  ["Rollout", "Hawai‘i (O‘ahu, Maui, Big Island, Kaua‘i) 2026 → West Coast (CA, NV)"],
  ["Website", "https://pogspirits.com"],
  ["Press contact", "inquire@pogspirits.com"],
  ["Trademark", "POG® is a trademark of Meadow Gold Dairies of Hawai‘i"],
];

const CopyBtn = ({ text, label = "Copy", testid }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Copied to clipboard.");
        setTimeout(() => setCopied(false), 2200);
      }}
      className="btn-ghost text-[10px] px-4 py-2"
    >
      {copied ? "Copied" : label}
    </button>
  );
};

const PressKit = () => {
  return (
    <div className="min-h-screen bg-[#14100D] text-[#EFE7D6]">
      <Helmet>
        <title>POG Spirits Press Kit — Logos & Brand Assets</title>
        <meta
          name="description"
          content="Official POG Spirits press kit. Downloadable brand logo, product shots (POG Vodka 750ml, POG Spiked Island Punch RTD), boilerplate copy, fact sheet, and media contact. For journalists, retailers, and partners."
        />
        <link rel="canonical" href={`${CANONICAL}/press-kit`} />
        <meta property="og:title" content="POG Spirits Press Kit — Logos, Product Shots, Boilerplate" />
        <meta property="og:description" content="Everything journalists and retailers need to write about POG Spirits: high-resolution logos, product photography, boilerplate copy, brand fact sheet, and press contact." />
        <meta property="og:url" content={`${CANONICAL}/press-kit`} />
        <meta property="og:image" content={`${CANONICAL}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "POG Spirits Press Kit",
            "url": `${CANONICAL}/press-kit`,
            "description": "Official POG Spirits press kit — downloadable logos, product shots, boilerplate, and press contact.",
            "isPartOf": { "@type": "WebSite", "url": CANONICAL, "name": "POG Spirits" },
            "about": { "@type": "Organization", "name": "POG Spirits", "url": CANONICAL },
          })}
        </script>
      </Helmet>

      {/* Header nav */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#14100D]/90 backdrop-blur-xl border-b border-[#EFE7D6]/10">
        <div className="max-w-[1400px] mx-auto px-8 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <PogLogo size={28} />
            <span className="font-body italic text-[#EFE7D6] tracking-wide">Spirits</span>
          </Link>
          <Link
            to="/"
            className="font-body text-[11px] tracking-[0.28em] uppercase text-[#EFE7D6]/75 hover:text-[#E85A15] transition-colors link-underline"
            data-testid="press-kit-back-home"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      <main className="pt-40 pb-32">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16">
          {/* Hero */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="mb-24 md:mb-32"
            data-testid="press-kit-hero"
          >
            <div className="flex items-center gap-6 mb-10">
              <span className="counter">— Press Kit</span>
              <span className="w-16 hairline-strong h-px inline-block" />
              <span className="overline">For Media & Partners</span>
            </div>
            <h1 className="display-tight text-5xl md:text-8xl mb-10">
              Everything you need
              <br />
              <span className="italic text-[#DCCFB4]">to tell our story.</span>
            </h1>
            <p className="font-body text-[#DCCFB4]/85 text-lg md:text-xl leading-[1.7] max-w-3xl font-light">
              Journalists, retailers, and partners — you'll find high-resolution logos,
              product shots, boilerplate copy, and a full fact sheet below. Everything
              is free to use in editorial and commercial contexts covering POG Spirits.
              Please credit "POG Spirits" or "Meadow Gold / POG Spirits" as the source.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="/api/public/press-kit"
                className="btn-primary is-solid"
                data-testid="press-kit-json"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download JSON Data
              </a>
              <CopyBtn
                text="inquire@pogspirits.com"
                label="Copy Press Email"
                testid="press-kit-copy-email"
              />
            </div>
          </motion.section>

          {/* Downloadable assets */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="mb-24"
            data-testid="press-kit-assets"
          >
            <div className="flex items-center gap-6 mb-10">
              <span className="counter">01</span>
              <span className="w-16 hairline-strong h-px inline-block" />
              <h2 className="overline">Downloadable Assets</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {ASSETS.map((a, i) => (
                <div
                  key={a.id}
                  className="group border border-[#EFE7D6]/12 hover:border-[#E85A15]/50 transition-colors"
                  data-testid={`asset-${a.id}`}
                >
                  <div
                    className="aspect-square relative overflow-hidden"
                    style={{
                      background:
                        a.tone === "og"
                          ? "#F26B1F"
                          : "radial-gradient(ellipse at 50% 40%, #2a1f18 0%, #0f0a07 80%)",
                    }}
                  >
                    <img
                      src={a.preview}
                      alt={a.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-contain p-6"
                    />
                  </div>
                  <div className="p-5">
                    <div className="font-display text-lg mb-1 tracking-tight">{a.title}</div>
                    <div className="font-body text-[11px] uppercase tracking-[0.2em] text-[#DCCFB4]/60 mb-4">
                      {a.format}
                    </div>
                    <a
                      href={a.url}
                      download
                      className="btn-ghost text-[10px] px-4 py-2 block text-center"
                      data-testid={`download-${a.id}`}
                    >
                      Download
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Boilerplate copy */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="mb-24 grid md:grid-cols-2 gap-10"
            data-testid="press-kit-boilerplate"
          >
            <div>
              <div className="flex items-center gap-6 mb-10">
                <span className="counter">02</span>
                <span className="w-16 hairline-strong h-px inline-block" />
                <h2 className="overline">Boilerplate — Short</h2>
              </div>
              <p className="font-body text-[#EFE7D6]/90 text-base leading-[1.85] font-light">
                {BOILERPLATE_SHORT}
              </p>
              <div className="mt-6">
                <CopyBtn text={BOILERPLATE_SHORT} label="Copy Short Boilerplate" testid="copy-boiler-short" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-6 mb-10">
                <span className="counter">03</span>
                <span className="w-16 hairline-strong h-px inline-block" />
                <h2 className="overline">Boilerplate — Long</h2>
              </div>
              <p className="font-body text-[#EFE7D6]/90 text-base leading-[1.85] font-light">
                {BOILERPLATE_LONG}
              </p>
              <div className="mt-6">
                <CopyBtn text={BOILERPLATE_LONG} label="Copy Long Boilerplate" testid="copy-boiler-long" />
              </div>
            </div>
          </motion.section>

          {/* Fact sheet */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="mb-24"
            data-testid="press-kit-fact-sheet"
          >
            <div className="flex items-center gap-6 mb-10">
              <span className="counter">04</span>
              <span className="w-16 hairline-strong h-px inline-block" />
              <h2 className="overline">Fact Sheet</h2>
            </div>
            <dl className="border-t border-[#EFE7D6]/12">
              {FACT_SHEET.map(([k, v]) => (
                <div
                  key={k}
                  className="grid grid-cols-12 gap-6 py-5 border-b border-[#EFE7D6]/12"
                >
                  <dt className="col-span-12 sm:col-span-4 overline text-[#E85A15]/90">
                    {k}
                  </dt>
                  <dd className="col-span-12 sm:col-span-8 font-body text-[#EFE7D6]/90 text-base leading-relaxed font-light">
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
          </motion.section>

          {/* API access */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="mb-24"
            data-testid="press-kit-api"
          >
            <div className="flex items-center gap-6 mb-10">
              <span className="counter">05</span>
              <span className="w-16 hairline-strong h-px inline-block" />
              <h2 className="overline">Public API — For Aggregators & AI</h2>
            </div>
            <p className="font-body text-[#DCCFB4]/80 text-base leading-[1.75] font-light max-w-3xl mb-6">
              All POG Spirits brand data is available as public JSON — free to consume,
              cache, and reference. No key required. Please cite <code className="text-[#E85A15]">pogspirits.com</code> as the source.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                ["/api/public/brand", "Brand info, tagline, boilerplate, contacts"],
                ["/api/public/products", "All products (Vodka + RTD)"],
                ["/api/public/products/pog-vodka", "POG Vodka details"],
                ["/api/public/products/pog-spiked-island-punch", "POG Spiked Island Punch details"],
                ["/api/public/cocktails", "All signature cocktails"],
                ["/api/public/faq", "Frequently asked questions"],
                ["/api/public/press-kit", "This press kit as JSON"],
                ["/api/public/rss.xml", "RSS feed (for aggregators)"],
              ].map(([path, label]) => (
                <a
                  key={path}
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-[#EFE7D6]/12 hover:border-[#E85A15]/60 p-5 transition-colors group"
                  data-testid={`api-endpoint-${path.split('/').pop()}`}
                >
                  <div className="font-mono text-[13px] text-[#E85A15] mb-1 tracking-tight break-all">
                    GET {path}
                  </div>
                  <div className="font-body text-sm text-[#DCCFB4]/70 font-light">
                    {label}
                  </div>
                </a>
              ))}
            </div>
          </motion.section>

          {/* Gov warning */}
          <div className="border-t border-[#EFE7D6]/12 pt-10">
            <div className="overline mb-3 text-[#EFE7D6]/40">Government Warning</div>
            <p className="font-body text-[10px] uppercase tracking-[0.15em] text-[#DCCFB4]/45 leading-[1.7] max-w-4xl">
              {GOV_WARNING}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PressKit;
